// בקשות למנהל הבינה (OpenSpec conversation-admin-requests, 2026-10-21): admin.request של אייבי → sys.admin_request.
// אישור/דחייה — אדם בלבד (POST /api/admin-requests/:id/decide); סגירה — מנהל הבינה (admin.request.resolve).
import { z } from 'zod';
import { Id, Json, Ts, rowBase } from './common.ts';

export const AdminRequestKind = z.enum(['index', 'summary', 'tree', 'interface', 'access', 'other']);
export type AdminRequestKind = z.infer<typeof AdminRequestKind>;
export const AdminRequestStatus = z.enum(['open', 'in_progress', 'done', 'declined']);
export type AdminRequestStatus = z.infer<typeof AdminRequestStatus>;

/** מה שאייבי מעבירה (קלט הכלי, אחרי נרמול). בלי ערכים מהנתונים — מדדים ושמות בלבד (הכרעה 9). */
export const AdminRequestInput = z.object({
  kind: AdminRequestKind,
  titleHe: z.string().min(3).max(200),
  needHe: z.string().min(1).max(4000),
  findingsHe: z.string().max(8000).default(''),
  spreads: z.array(z.string().max(80)).max(20).default([]),
  artifacts: z.array(z.string().max(80)).max(20).default([]),
  world: z.string().max(64).nullable().optional(),
});
export type AdminRequestInput = z.input<typeof AdminRequestInput>;

/**
 * כפילות (הכרעה 3, "אותו נושא ואותן פריסות"): ההפניות הן לרוב הפריסות שאייבי בדקה, לא נושא הבקשה — בקשה על ביקורי לקוח ובקשה על
 * מלאי, שתיהן אחרי בדיקת "לקוחות" ו"חשבוניות", הן לא אותה בקשה (bench זיכרון ארגוני, 2026-10-26). לכן: אינדקס וסיכום על אותן
 * פריסות — הפריסה היא הנושא, מצטרפים; כל השאר (ובקשה בלי הפניות) — רק כשגם הכותרות קרובות: לפחות 2 מילים משותפות, ולפחות חצי
 * מהמילים של הכותרת הקצרה. מילה = רצף אותיות/ספרות, בלי גרש/גרשיים ובלי מילים של 1–2 אותיות (עם, של, על).
 */
export const titleWords = (s: string): Set<string> =>
  new Set(
    s
      .toLowerCase()
      .replace(/["'״׳`]/g, '')
      .split(/[^\p{L}\p{N}]+/u)
      .filter((w) => w.length > 2),
  );
export function sameTitleTopic(a: string, b: string): boolean {
  const A = titleWords(a);
  const B = titleWords(b);
  if (!A.size || !B.size) return a.trim() === b.trim();
  let shared = 0;
  for (const w of A) if (B.has(w)) shared++;
  return shared >= Math.min(2, A.size, B.size) && shared * 2 >= Math.min(A.size, B.size);
}
/** האם בקשה חדשה מצטרפת לבקשה פעילה עם אותו kind + world + refs (ההשוואה של אלה ב-SQL). */
export const joinsRequest = (kind: AdminRequestKind, hasRefs: boolean, newTitle: string, openTitle: string): boolean =>
  (hasRefs && (kind === 'index' || kind === 'summary')) || sameTitleTopic(newTitle, openTitle);

/** החלטת אדם בפאנל. */
export const AdminRequestDecide = z.object({ decision: z.enum(['approve', 'decline']), noteHe: z.string().max(2000).optional() });
export type AdminRequestDecide = z.infer<typeof AdminRequestDecide>;

/** sys.admin_request */
export const AdminRequest = z.object({
  ...rowBase,
  kind: AdminRequestKind,
  titleHe: z.string(),
  needHe: z.string(),
  findingsHe: z.string(),
  refs: Json,
  worldKey: z.string().nullable(),
  requestedBy: z.array(Id),
  conversationId: Id.nullable(),
  turnId: Id.nullable(),
  status: AdminRequestStatus,
  decidedBy: Id.nullable(),
  decidedAt: Ts.nullable(),
  decisionNoteHe: z.string().nullable(),
  adminConversationId: Id.nullable(),
  resultHe: z.string().nullable(),
  resolvedAt: Ts.nullable(),
  seen: Json,
  readyAt: Ts.nullable(),
});
export type AdminRequest = z.infer<typeof AdminRequest>;
